// import { Component, Prop, Vue } from 'vue-property-decorator';
import { WxRequest as request } from '../../lib/request';
import { DataType, FieldAlignment, WidgetAppearance, ColumnAppearance, FieldDesc, Options } from '../wxFieldDesc';
import VueI18n from 'vue-i18n';

export interface IDataAware {
    dataDesc: DataDesc;
}

interface TableDefinition {
    ENTITY: string;
    CAPTION: string;
    PRIMARYKEY: string;
    SELECTPROC: string;
    DETAILPROC: string | null;
    UPDATEPROC: string | null;
    INSERTPROC: string | null;
    DELETEPROC: string | null;
    UPDATEPRIV: string | null;
    INSERTPRIV: string | null;
    DELETEPRIV: string | null;
}

interface FieldDefinition {
    ENTITY: string;
    FIELDNAME: string;
    FIELDTYPE: string;
    FIELDSIZE: number | null;
    ALIGNMENT: string | null;
    CAPTION: string;
    COLUMNSTYLE: number;
    UPDATESTYLE: number;
    INSERTSTYLE: number;
    DEFAULTVALUE: string | null;
    OPTIONS: string | null;
    INPUTMASK: string | null;
    FIELDHINT: string | null;
    CONTEXT: string | null;
}

export enum DatasetState {
    Created = 0,
    Named = 1,
    Loaded = 2,
    Busy = 3,
}

export class DataDesc {
    private xState: DatasetState = DatasetState.Created;
    private xCaption = 'NoName';
    private xEntity = 'entity';
    private xPrimaryKey = 'GUID';
    private xSelectProc = 'selectentity';
    private xInsertProc = '';
    private xUpdateProc = '';
    private xDeleteProc = '';
    private xDefault = {};
    private xRecord = {};
    private xFields: FieldDesc[] = [];
    private xRecords: object[] = [];
    private xLoading = false;
    private xAction = '';

    constructor(config: string, entity?: string, primaryKey?: string) {
        this.xCaption = config;
        if (!(entity === undefined)) {
            this.xEntity = entity || this.xEntity;
        }
        if (!(primaryKey === undefined)) {
            this.xPrimaryKey = primaryKey || this.xPrimaryKey;
        }
        if (this.caption.charAt(0) !== '$') {
            this.xSelectProc = config.replace('config', 'select');
        }
    }
    public get state() {
        return this.xState;
    }
    public get caption() {
        return this.xCaption;
    }
    public set caption(value: string) {
        this.xCaption = value;
    }
    public get entity() {
        return this.xEntity;
    }
    public set entity(value: string) {
        this.xEntity = value;
    }
    public get primaryKey() {
        return this.xPrimaryKey;
    }
    public set primaryKey(value: string) {
        this.xPrimaryKey = value;
    }
    public get selectProc() {
        return this.xSelectProc;
    }
    public set selectProc(value: string) {
        this.xSelectProc = value;
        this.xState = DatasetState.Created;
    }
    public get insertProc() {
        return this.xInsertProc;
    }
    public set insertProc(value: string) {
        this.xInsertProc = value;
    }
    public get updateProc() {
        return this.xUpdateProc;
    }
    public set updateProc(value: string) {
        this.xUpdateProc = value;
    }
    public get deleteProc() {
        return this.xDeleteProc;
    }
    public set deleteProc(value: string) {
        this.xDeleteProc = value;
    }
    public get default() {
        return this.xDefault;
    }
    public get record() {
        return this.xRecord;
    }
    public set record(record: object) {
        this.xRecord = record;
    }
    public get records() {
        return this.xRecords;
    }
    public set records(records: object[]) {
        this.xRecords = records;
    }
    public get fields() {
        return this.xFields;
    }
    public set fields(fields: FieldDesc[]) {
        this.xFields = fields;
    }
    public get loading() {
        return this.xLoading;
    }
    public set loading(loading: boolean) {
        this.xLoading = loading;
    }
    public get message() {
        return this.xAction;
    }
    public set message(message: string) {
        this.xAction = message;
    }
    public get recordDesc() {
        return 'Entity';
    }
    /* basic record handling */
    public insertRecord(record: object, i18n?: VueI18n) {
        if (record) {
            this.records.push(record);
            if (i18n) {
                this.message = i18n.t('$common.ITEM_INSERTED', [this.recordDesc]).toString();
            }
        } else {
            if (i18n) {
                this.message = i18n.t('$common.ITEM_NULL_NOT_ALLOWED', [this.caption]).toString();
            }
        }
    }
    public updateRecord(record: object, i18n?: VueI18n) {
        if (record) {
            this.record = Object.assign(this.xRecord, record);
            if (i18n) {
                this.message = i18n.t('$common.ITEM_UPDATED', [this.recordDesc]).toString();
            }
        } else {
            if (i18n) {
                this.message = i18n.t('$common.ITEM_NULL_NOT_ALLOWED', [this.caption]).toString();
            }
        }
    }
    public insertOrUpdate(record: object, isInsert: boolean, i18n?: VueI18n) {
        if (record) {
            if (isInsert) {
                this.insertRecord(record, i18n);
            } else {
                this.updateRecord(record, i18n);
            }
        }
    }
    /* external utilities */
    public fieldByName(aFieldName: string) {
        return this.fields.find((f) => f.fieldname === aFieldName);
    }
    public addColumn(
        fieldname: string,
        datatype: DataType,
        size?: number,
        caption?: string,
        columnStyle?: ColumnAppearance,
        alignment?: FieldAlignment
    ): DataDesc {
        this.fields.push(new FieldDesc(fieldname, datatype, caption, columnStyle, alignment, size));
        return this;
    }
    public addField(
        fieldname: string,
        datatype: DataType,
        size?: number,
        caption?: string,
        columnStyle?: ColumnAppearance,
        alignment?: FieldAlignment,
        updateStyle?: WidgetAppearance,
        defaultValue?: string,
        hint?: string,
        options?: string,
        context?: string
    ): DataDesc {
        const field = new FieldDesc(fieldname, datatype, caption, columnStyle, alignment, size, hint);
        field.updateStyle = updateStyle || WidgetAppearance.Hidden;
        field.defaultValue = defaultValue || '';
        const jsonParsedOptions = options ? JSON.parse(options) : undefined;
        field.options = jsonParsedOptions || new Options();
        field.context = context || '';
        this.fields.push(field);
        return this;
    }
    public async configure() {
        if (this.caption.charAt(0) !== '$') {
            const proc = this.caption;
            const mod = await request().execute(proc, { MODE: 1 });
            const success1 = !!mod.data && mod.data instanceof Array;
            if (success1) {
                this.configureModel(mod.data);
            }
            const res = await request().execute(proc, { MODE: 2 });
            const success2 = !!res.data && res.data instanceof Array;
            if (success2) {
                this.configureField(res.data);
            }
        } else {
            // we assume that fields and selectProc were already set
            this.xState = DatasetState.Loaded;
        }
    }

    public pagination(pager: any): void {
        for (const field of this.fields) {
            if (field.columnStyle === ColumnAppearance.Ascending) {
                pager.sortBy.push(field.fieldname);
                pager.sortDesc.push(false);
            } else if (field.columnStyle === ColumnAppearance.Descending) {
                pager.sortBy.push(field.fieldname);
                pager.sortDesc.push(true);
            }
        }
        pager.initialized = true;
    }
    private configureModel(models: object[]) {
        const rawmodel = models.find((m) => (m as TableDefinition).ENTITY === this.entity);
        if (rawmodel) {
            const model = rawmodel as TableDefinition;
            this.caption = model.CAPTION;
            this.primaryKey = model.PRIMARYKEY;
            this.selectProc = model.SELECTPROC;
            this.updateProc = model.UPDATEPROC === null ? '' : model.UPDATEPROC;
            this.insertProc = model.INSERTPROC === null ? this.updateProc : model.INSERTPROC;
            this.deleteProc = model.DELETEPROC === null ? '' : model.DELETEPROC;
        }
    }
    private configureField(fields: object[]) {
        for (const rawfield of fields) {
            const field = rawfield as FieldDefinition;
            const exist = this.fields.find((f) => field.ENTITY === this.entity && field.FIELDNAME === f.fieldname);
            if (!exist) {
                const typename = field.FIELDTYPE;
                let datatype: DataType = DataType.String;
                if (typename.startsWith('st')) {
                    datatype = DataType.String;
                } else if (typename.startsWith('gu')) {
                    datatype = DataType.Guid;
                } else if (typename.startsWith('bo')) {
                    datatype = DataType.Boolean;
                } else if (typename.startsWith('ind')) {
                    datatype = DataType.Indicator;
                } else if (typename.startsWith('int')) {
                    datatype = DataType.Integer;
                } else if (typename.startsWith('am')) {
                    datatype = DataType.Amount;
                } else if (typename.startsWith('fl')) {
                    datatype = DataType.Float;
                } else if (typename === 'date') {
                    datatype = DataType.Date;
                } else if (typename.startsWith('datet')) {
                    datatype = DataType.DateTime;
                } else if (typename.startsWith('ti')) {
                    datatype = DataType.Time;
                } else if (typename.startsWith('du')) {
                    datatype = DataType.Duration;
                } else if (typename.startsWith('te')) {
                    datatype = DataType.Text;
                } else if (typename.startsWith('ba')) {
                    datatype = DataType.Base64;
                } else if (typename.startsWith('ic')) {
                    datatype = DataType.Icon;
                } else if (typename.startsWith('ac')) {
                    datatype = DataType.Action;
                } else if (typename.startsWith('im')) {
                    datatype = DataType.IMEI;
                }
                const align = field.ALIGNMENT;
                let alignment: FieldAlignment = FieldAlignment.Left;
                if (align != null && align.startsWith('ri')) {
                    alignment = FieldAlignment.Right;
                } else if (align != null && align.startsWith('ce')) {
                    alignment = FieldAlignment.Centered;
                }

                this.addField(
                    field.FIELDNAME,
                    datatype,
                    field.FIELDSIZE === null ? 0 : field.FIELDSIZE,
                    field.CAPTION === null ? field.FIELDNAME : field.CAPTION,
                    field.COLUMNSTYLE > 0 && field.COLUMNSTYLE <= 3 ? (field.COLUMNSTYLE as ColumnAppearance) : ColumnAppearance.Hidden,
                    alignment,
                    field.UPDATESTYLE > 0 && field.UPDATESTYLE <= 3 ? (field.UPDATESTYLE as WidgetAppearance) : WidgetAppearance.Hidden,
                    field.DEFAULTVALUE === null ? '' : field.DEFAULTVALUE,
                    field.FIELDHINT === null ? '' : field.FIELDHINT,
                    field.OPTIONS === null ? '' : field.OPTIONS,
                    field.CONTEXT === null ? '' : field.CONTEXT
                );
            }
            this.xState = DatasetState.Loaded;
        }
    }
}
